import { Ellipsis, EllipsisVertical } from "lucide-react";
import React, { MouseEvent, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Updater, useImmer } from "use-immer";
import { v4 } from "uuid";

import { IgIconButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import {
  GetScriptStatusResponseBodyType,
  OrganizationDto,
  TabEnumFe,
} from "@intelligems/ig-types";
import { Menu, MenuItem } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useDeleteExperience } from "@src/api/experience-actions/delete-experience";
import { useDuplicateExperience } from "@src/api/experiences/duplicate-experience";
import { useRestartExperience } from "@src/api/experiences/restart-experience";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetHistory } from "@src/api/histories/get-history";
import { processesKeys } from "@src/api/queryKeys";
import { useGetExperienceSchedules } from "@src/api/schedules/get-schedules-by-experience-id";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { UpgradePlanText } from "@src/components/LimitAccess/access-text";
import { checkBooleanScope } from "@src/components/LimitAccess/check-scopes";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { ScopedComponentWithDisable } from "@src/components/LimitAccess/ScopedComponentWithDisable";
import { ScopedCreateExperienceWithDisabled } from "@src/components/LimitAccess/ScopedCreateExperienceWithDisabled";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import {
  isRolloutEnabled,
  onRolloutClick,
  renderRolloutButton,
} from "@src/features/experiments/utils/experienceActionUtils";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import { useDispatch, useSelector } from "@src/store";
import { openDrawer } from "@src/store/slices/drawers";
import { setExperienceTabWithoutRedirect } from "@src/store/slices/experiences";
import { getScheduleExperienceInfo } from "@src/store/slices/experiences/selectors/getScheduleExperienceInfo";
import { Experience } from "@src/store/slices/experiences/types";
import { setDialog } from "@src/store/slices/global";
import { generatePreviewLink } from "@src/utils/generatePreviewLink";
import {
  mpClickAnalyticsButton,
  mpClickButton,
  mpClickDuplicateExperienceButton,
  mpClickEditExperienceButton,
} from "@src/utils/mixpanel";
import { useQueryClient } from "@tanstack/react-query";

import { getConfirmedIntegrationHistoryAction } from "./utils";

export interface MoreMenuExperimentProps {
  scriptStatus: GetScriptStatusResponseBodyType | null;
  organization: OrganizationDto;
  experience: Experience;
  setCurrExperience?: Updater<Experience | undefined>;
  renderLocation: "overview" | "experience";
  tabClicked?: string;
  analyticsDataNotAvailable?: boolean;
  isDataLoading?: number;
}

export const MoreMenuExperiment = ({
  organization,
  experience,
  scriptStatus,
  setCurrExperience,
  renderLocation,
  tabClicked,
  analyticsDataNotAvailable,
  isDataLoading,
}: MoreMenuExperimentProps) => {
  const experienceId = experience.id!;
  const searchParams = new URLSearchParams(window.location.search);

  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);
  const downloadPriceCSVEnabled = useConfiguration("enable_price_csv_download");
  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  )!.value;

  const duplicateExperience = useDuplicateExperience("experiment");
  const restartExperiment = useRestartExperience(experienceId);
  const deleteExperience = useDeleteExperience(experience, "experiment");
  const queryClient = useQueryClient();

  const canAnalyzeExperiment = useCheckSubscriptionStatus() || isAdmin;
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    mpClickButton("experiment-page-more-button");
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const canCreateShippingTest = useMemo(() => {
    if (experiencesList.isSuccess) {
      return (
        experiencesList.data.experiencesList.filter(
          (exp) => exp.testTypes.hasTestShipping && exp.status !== "ended"
        ).length === 0
      );
    } else return true;
  }, [experiencesList]);

  const editPath = useMemo(() => {
    return `${EXPERIMENT_ROUTES.Experiment}/${experienceId}?action=edit&type=${experience.type}&tab=${
      experience.status === "pending"
        ? TabEnumFe.TEST_GROUPS
        : TabEnumFe.ANALYTICS
    }`;
  }, [experience]);

  const onRolloutClicked = () => {
    handleClose();
    if (setCurrExperience) {
      setCurrExperience(experience);
    }
    onRolloutClick(experience, storeCurrencyConfig, dispatch);
  };

  const onDeleteExperience = () => {
    dispatch(setDialog({ dialog: "confirmExperimentDeletion", open: true }));
    handleClose();
  };

  return (
    <>
      <IgTooltip title={"View More Options"}>
        <IgIconButton
          size={"small"}
          id="experiment-page-more-button"
          aria-controls="more-positioned-menu"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          loading={deleteExperience.isPending || duplicateExperience.isPending}
        >
          {renderLocation === "experience" ? (
            <EllipsisVertical />
          ) : (
            <Ellipsis />
          )}
        </IgIconButton>
      </IgTooltip>
      <Menu
        id="more-positioned-menu"
        aria-labelledby="more-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {renderLocation === "overview" && (
          <Link
            style={{ textDecoration: "none", color: "black" }}
            to={editPath}
            state={{ id: experienceId }}
            onClick={() => {
              mpClickEditExperienceButton();
              handleClose();
            }}
          >
            <MenuItem>
              <IgTypography>
                {experience.status === "ended" || experience.isExternal
                  ? "View"
                  : "Edit"}
              </IgTypography>
            </MenuItem>
          </Link>
        )}
        <ScheduleOption
          experience={experience}
          onClick={handleClose}
          setCurrExperience={setCurrExperience}
        />
        {experience.startedAtTs &&
        experience.startedAtTs > 0 &&
        canAnalyzeExperiment &&
        renderLocation === "overview" ? (
          <Link
            style={{ textDecoration: "none", color: "black" }}
            onClick={() => {
              mpClickAnalyticsButton(
                experience.name,
                experienceId,
                "MoreMenu analytics button"
              );
            }}
            to={`${EXPERIMENT_ROUTES.Experiment}/${experienceId}?action=edit&type=${experience.type}&tab=${TabEnumFe.ANALYTICS}`}
          >
            <MenuItem>
              <IgTypography>Analyze</IgTypography>
            </MenuItem>
          </Link>
        ) : null}
        {experienceId !== undefined &&
          !experience.isExternal &&
          (!experience.testTypes.hasTestShipping || canCreateShippingTest) && (
            <ScopedCreateExperienceWithDisabled
              experience={experience}
              debugId={"duplicate-experiment-menu-button"}
              customDisabledTooltipMessage={UpgradePlanText}
            >
              <MenuItem
                style={{ textDecoration: "none", color: "black" }}
                onClick={() => {
                  mpClickDuplicateExperienceButton();
                  queryClient.setQueryData(processesKeys.one(organization.id), {
                    processes: [
                      {
                        id: v4(),
                        orgId: organization.id,
                        type: "duplicateExperience",
                        severity: "info",
                        title: `Duplicating ${experience.name}`,
                        isLoading: true,
                      },
                    ],
                  });
                  duplicateExperience.mutate({
                    body: undefined,
                    pathParams: { experienceId },
                    queryParams: undefined,
                  });
                  handleClose();
                }}
                disabled={duplicateExperience.isPending}
              >
                <IgTypography>Duplicate</IgTypography>
              </MenuItem>
            </ScopedCreateExperienceWithDisabled>
          )}
        {experienceId !== undefined && (
          <MenuItem
            onClick={() => {
              if (setCurrExperience) {
                setCurrExperience(experience);
              }
              if (!scriptStatus?.exists) {
                dispatch(
                  setDialog({
                    dialog: "redirectToPluginDialog",
                    open: true,
                    props: {
                      id: experienceId,
                      experience,
                      path: generatePreviewLink({ organization, experience }),
                    },
                  })
                );
              } else {
                dispatch(
                  setDialog({
                    dialog: "previewExperiment",
                    open: true,
                    props: {
                      id: experienceId,
                      path: generatePreviewLink({ organization, experience }),
                    },
                  })
                );
              }
              handleClose();
            }}
          >
            <IgTypography>Preview</IgTypography>
          </MenuItem>
        )}
        {experienceId !== undefined &&
        shouldDuplicateProducts &&
        experience.testTypes.hasTestPricing ? (
          <MenuItem
            onClick={() => {
              if (renderLocation === "overview") {
                dispatch(setExperienceTabWithoutRedirect("mods"));
                void navigate(
                  `${EXPERIMENT_ROUTES.Experiment}/${experienceId}?action=edit&type=${experience.type}&tab=${TabEnumFe.MODS}`,
                  {
                    state: { tab: "mods", showDuplicateProducts: true },
                  }
                );
              } else {
                dispatch(
                  setDialog({
                    dialog: "duplicateProducts",
                    open: true,
                    props: {
                      showStartTestRow: false,
                    },
                  })
                );
              }
              handleClose();
            }}
          >
            <IgTypography>View Duplicates</IgTypography>
          </MenuItem>
        ) : null}
        {renderRolloutButton(
          tabClicked || "",
          analyticsDataNotAvailable || false,
          isDataLoading || 0,
          experience,
          checkBooleanScope("personalizations", organization.configurations),
          "Menu"
        ) ? (
          <ScopedStartExperienceWithDisabled
            experience={experience}
            debugId={"rollout-experiment-menu-button"}
            customDisabledTooltipMessage={UpgradePlanText}
          >
            <MenuItem
              disabled={
                !isRolloutEnabled(
                  experience,
                  checkBooleanScope(
                    "personalizations",
                    organization.configurations
                  )
                )
              }
              onClick={onRolloutClicked}
            >
              Roll out
            </MenuItem>
          </ScopedStartExperienceWithDisabled>
        ) : null}
        {experienceId !== undefined &&
          renderLocation === "overview" &&
          !experience.isExternal &&
          experience.status !== "started" && (
            <MenuItem
              onClick={() => {
                if (setCurrExperience) {
                  setCurrExperience(experience);
                }
                onDeleteExperience();
              }}
            >
              <IgTypography>Delete</IgTypography>
            </MenuItem>
          )}
        {experienceId !== undefined &&
          (downloadPriceCSVEnabled?.value || isAdmin) &&
          experience.testTypes.hasTestPricing && (
            <MenuItem
              onClick={() => {
                if (setCurrExperience) {
                  setCurrExperience(experience);
                }
                dispatch(
                  setDialog({
                    dialog: "downloadPriceCsv",
                    open: true,
                  })
                );
                handleClose();
              }}
            >
              <IgTypography>Get Price CSV</IgTypography>
            </MenuItem>
          )}
        {experienceId !== undefined && (
          <MenuItem
            onClick={() => {
              if (setCurrExperience) {
                setCurrExperience(experience);
              }
              dispatch(
                openDrawer({
                  type: "experienceInfo",
                  experience,
                  isUsedInTest: {
                    name: undefined,
                    id: undefined,
                  },
                })
              );
              handleClose();
            }}
          >
            <IgTypography>Show Info</IgTypography>
          </MenuItem>
        )}
        {isAdmin &&
          experience.status === "ended" &&
          experience.type !== "shipping" && (
            <MenuItem
              onClick={() => {
                restartExperiment.mutate({
                  body: undefined,
                  pathParams: { experienceId },
                  queryParams: undefined,
                });
              }}
            >
              <IgTypography>Reset to Paused</IgTypography>
            </MenuItem>
          )}
        {experienceId !== undefined && (
          <MenuItem
            onClick={() => {
              dispatch(
                openDrawer({
                  type: "history",
                  experienceType: "experiment",
                  experienceInfo:
                    renderLocation === "overview" ? experience : undefined,
                })
              );
              handleClose();
            }}
          >
            <IgTypography>View History</IgTypography>
          </MenuItem>
        )}
        {renderLocation === "experience" &&
          searchParams.get("tab") === TabEnumFe.ANALYTICS &&
          experience.status !== "pending" && (
            <MenuItem
              onClick={() => {
                dispatch(setDialog({ dialog: "dataExport", open: true }));
                handleClose();
              }}
            >
              <IgTypography>Export Data</IgTypography>
            </MenuItem>
          )}
      </Menu>
    </>
  );
};

interface ScheduleOptionProps {
  experience: Experience;
  setCurrExperience?: Updater<Experience | undefined>;
  onClick: () => void;
}

const ScheduleOption = ({
  experience,
  setCurrExperience,
  onClick,
}: ScheduleOptionProps) => {
  const experienceId = experience.id!;
  const dispatch = useDispatch();
  const schedules = useGetExperienceSchedules(experienceId);
  const history = useGetHistory({
    body: undefined,
    pathParams: { experienceId },
    queryParams: undefined,
  });
  const scheduleInfo = useSelector(
    getScheduleExperienceInfo(experience, history.isSuccess)
  );

  return (
    <ScopedStartExperienceWithDisabled
      debugId={"schedule-experience-button"}
      experience={experience}
      customDisabledTooltipMessage={UpgradePlanText}
      customEnabledTooltipMessage={scheduleInfo.reason}
    >
      <MenuItem
        disabled={!scheduleInfo.showDialog}
        onClick={() => {
          if (setCurrExperience) {
            setCurrExperience(experience);
          }
          if (
            !getConfirmedIntegrationHistoryAction(history.data?.history || [])
          ) {
            dispatch(
              setDialog({
                dialog: "confirmIgChecklist",
                open: true,
                props: { isAdminOverride: false, isSchedule: true },
              })
            );
          } else {
            dispatch(
              setDialog({
                dialog: "scheduleTest",
                open: true,
                props: {
                  experience,
                },
              })
            );
          }
          onClick();
        }}
      >
        <IgTypography>
          {schedules?.data?.schedules?.length
            ? "Edit or Cancel Schedule"
            : "Schedule Test"}
        </IgTypography>
      </MenuItem>
    </ScopedStartExperienceWithDisabled>
  );
};
